"use client";

// Reifegrad-Story: drei Stufen als horizontaler Fortschrittsbogen.
// Progressive Disclosure: alle drei Tiles sind sichtbar, aber nur die aktive
// zeigt die Detail-Bullets. Visuelle Hierarchie steigt von links (matt, flach)
// nach rechts (Zielbild — heller, stärkerer Rahmen/Glow). Default: Stufe 3 aktiv.

import { useState } from "react";
import { Hand, Sparkles, Bot } from "lucide-react";

type Stage = {
  icon: typeof Hand;
  label: string;
  title: string;
  promise: string;
  bullets: string[];
};

const stages: Stage[] = [
  {
    icon: Hand,
    label: "Stufe 1 / Manuell",
    title: "Du machst alles selbst.",
    promise: "Volle Kontrolle. Funktioniert auch, wenn keine AI verfügbar ist.",
    bullets: ["Quellen selbst sichten", "Text im Editor schreiben", "Sendung manuell planen"],
  },
  {
    icon: Sparkles,
    label: "Stufe 2 / AI-assistiert",
    title: "Mensch und AI arbeiten zusammen.",
    promise: "Co-Writing im Canvas, AI-Recherche als Begleiter — du entscheidest.",
    bullets: ["AI schlägt Quellen & Textideen vor", "Übernehmen, umschreiben oder ablehnen", "Faktencheck läuft im Hintergrund"],
  },
  {
    icon: Bot,
    label: "Stufe 3 / Agentisch orchestriert",
    title: "Agenten übernehmen die Pipeline.",
    promise: "Agenten übernehmen ganze Produktionsschritte — du steuerst, prüfst und gibst frei.",
    bullets: ["Recherche verdichten", "Skripte & TTS-Audio vorbereiten", "Nur Freigabe bleibt beim Team"],
  },
];

export function PrincipleSection() {
  const [active, setActive] = useState(2); // default: Zielbild

  return (
    <section className="principle relative bg-[var(--color-ink)] text-white py-[120px] md:py-[160px] overflow-hidden">
      {/* Ambient Glow — wandert mit dem aktiven Schritt für subtile Bewegung */}
      <div
        aria-hidden
        className="hidden md:block absolute top-1/2 w-[700px] h-[700px] rounded-full pointer-events-none transition-all duration-700 ease-out"
        style={{
          background: "radial-gradient(circle, var(--color-blue) 0%, transparent 70%)",
          opacity: 0.22,
          filter: "blur(60px)",
          left: `${15 + active * 30}%`,
          transform: "translate(-50%, -50%)",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 md:px-12">
        {/* Label */}
        <div className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#8B95FF] mb-6 flex items-center gap-3">
          <span className="w-6 h-px bg-[#8B95FF]" />
          <span>02 — Das Prinzip</span>
        </div>

        {/* Zitat */}
        <p className="font-serif text-[clamp(32px,3.5vw,48px)] leading-[1.2] tracking-[-0.015em] italic max-w-[900px] mb-16">
          &bdquo;Alles funktioniert{" "}
          <span className="text-[var(--color-blue)] font-medium">ohne AI</span>.
          <br />
          Alles wird besser{" "}
          <span className="text-[var(--color-blue)] font-medium">mit AI</span>.&ldquo;
        </p>

        {/* Stepper — zeigt Fortschrittsbogen */}
        <div className="flex items-center gap-2 mb-10" role="tablist" aria-label="Stufen des AI-Einsatzes">
          {stages.map((s, i) => {
            const isActive = i === active;
            return (
              <button
                key={s.label}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(i)}
                className="group flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-blue)] rounded-sm"
              >
                <span
                  className={`h-px transition-all duration-300 ${
                    isActive ? "w-12 bg-[var(--color-blue)]" : "w-6 bg-white/20 group-hover:bg-white/40"
                  }`}
                />
                <span className={isActive ? "text-white" : "text-white/40 group-hover:text-white/70"}>
                  0{i + 1} · {s.label.split(" / ")[1]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tiles — links matter, rechts als Zielbild heller */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5 items-stretch">
          {stages.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === active;
            const hierarchy = i; // 0 = leise, 2 = laut
            return (
              <article
                key={s.label}
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                className={`relative cursor-pointer flex flex-col rounded-2xl p-7 lg:p-8 transition-all duration-500 ${
                  isActive
                    ? "bg-white/[0.06] border-white/25 shadow-[0_0_40px_-8px_rgba(30,58,255,0.4)]"
                    : "bg-white/[0.02] border-white/[0.08] hover:bg-white/[0.04]"
                } border`}
                style={{
                  opacity: isActive ? 1 : 0.55 + hierarchy * 0.1,
                }}
              >
                {/* Head: Icon + Stufen-Nummer */}
                <div className="flex items-center justify-between mb-7">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      isActive
                        ? "border border-[var(--color-blue)] bg-[var(--color-blue)]/20"
                        : "border border-white/15 bg-white/[0.03]"
                    }`}
                  >
                    <Icon
                      size={22}
                      strokeWidth={1.5}
                      className={isActive ? "text-[var(--color-blue)]" : "text-white/50"}
                    />
                  </div>
                  <span
                    className={`font-mono text-[28px] leading-none transition-colors ${
                      isActive ? "text-white/40" : "text-white/15"
                    }`}
                  >
                    0{i + 1}
                  </span>
                </div>

                {/* Label */}
                <div
                  className={`font-mono text-[11px] tracking-[0.15em] uppercase mb-3 transition-colors ${
                    isActive ? "text-[#8B95FF]" : "text-white/30"
                  }`}
                >
                  {s.label}
                </div>

                {/* Title */}
                <h3 className="font-serif font-medium text-[clamp(20px,1.8vw,26px)] leading-[1.2] tracking-[-0.02em] mb-4">
                  {s.title}
                </h3>

                {/* Ein-Satz-Nutzen */}
                <p className={`text-[14.5px] leading-[1.55] transition-colors ${
                  isActive ? "text-white/75" : "text-white/45"
                }`}>
                  {s.promise}
                </p>

                {/* Progressive Disclosure: Bullets nur bei aktiv */}
                <div
                  className={`grid transition-all duration-500 ease-out ${
                    isActive ? "grid-rows-[1fr] opacity-100 mt-6 pt-6 border-t border-white/10" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <ul className="overflow-hidden space-y-2.5">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex gap-3 items-start">
                        <span className="font-mono text-[10px] text-[var(--color-blue)] shrink-0 w-4 pt-1 tracking-tighter">
                          {String(j + 1).padStart(2, "0")}
                        </span>
                        <span className="text-[13.5px] leading-[1.5] text-white/80">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
